import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookOpen, Sparkles, MessageCircleWarning, ListChecks, Plus, Edit3, Trash2, Save, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";

const MODULES = ["dashboard","customers","orders","shipments","employees","pdks","puantaj","maas","inventory","finance","tasks","whatsapp","settings","learning"];
const KINDS = [{ v: "yeni", l: "YENİ" }, { v: "gelistirildi", l: "GELİŞTİRİLDİ" }, { v: "degisti", l: "DEĞİŞTİ" }];
// Fallback statik rota listesi — /learning/available-routes çağrısı hata verirse
// admin form yine de çalışsın diye. Backend ALLOWED_ROUTES ile senkron tutulmalı.
const FALLBACK_ROUTES = [
  { path: "", label: "— Route Yok —" },
  { path: "/musteri", label: "Müşteriler" },
  { path: "/satislar", label: "Siparişler" },
  { path: "/sevkiyat", label: "Sevkiyat" },
  { path: "/stok", label: "Depo / Stok" },
  { path: "/uretim", label: "Üretim Takibi" },
  { path: "/calisan", label: "Çalışanlar" },
  { path: "/pdks/panel", label: "PDKS · Panel" },
  { path: "/puantaj/aylik", label: "Aylık Puantaj" },
  { path: "/maas/hesaplama", label: "Maaş Hesaplama" },
  { path: "/cari", label: "Cari Hesap / Finans" },
  { path: "/gorevler", label: "Görevler" },
  { path: "/whatsapp", label: "WhatsApp" },
  { path: "/ayarlar", label: "Ayarlar" },
];

function ArticleDialog({ article, onClose, onSaved }) {
  const [f, setF] = useState(() => article || {
    title: "", module: "settings", roles: ["*"], related_route: "", content_md: "", status: "active"
  });
  const [saving, setSaving] = useState(false);
  const [routes, setRoutes] = useState(FALLBACK_ROUTES);
  const [availableRoles, setAvailableRoles] = useState([{ code: "*", name: "Tüm Kullanıcılar" }]);

  // Admin dropdown'ları için gerçek listeler
  useEffect(() => {
    (async () => {
      try {
        const [{ data: rd }, { data: ro }] = await Promise.all([
          api.get("/learning/available-routes"),
          api.get("/learning/available-roles"),
        ]);
        if (rd?.items?.length) setRoutes([{ path: "", label: "— Route Yok —" }, ...rd.items]);
        if (ro?.items?.length) setAvailableRoles(ro.items);
      } catch { /* fallback zaten geçerli */ }
    })();
  }, []);

  const toggleRole = (code) => {
    setF((prev) => {
      const cur = prev.roles || [];
      // Tüm Kullanıcılar seçilirse diğerlerini temizle
      if (code === "*") return { ...prev, roles: cur.includes("*") ? [] : ["*"] };
      const without = cur.filter((c) => c !== "*");
      const has = without.includes(code);
      return { ...prev, roles: has ? without.filter((c) => c !== code) : [...without, code] };
    });
  };

  const save = async () => {
    if (!f.title || !f.content_md) { toast.error("Başlık ve içerik zorunlu"); return; }
    const payload = {
      ...f,
      related_route: f.related_route || null,
      roles: (f.roles && f.roles.length) ? f.roles : ["*"],
    };
    setSaving(true);
    try {
      if (article?.id) {
        await api.patch(`/learning/articles/${article.id}`, payload);
        toast.success("Eğitim güncellendi (version bump edildi)");
      } else {
        await api.post("/learning/articles", payload);
        toast.success("Eğitim oluşturuldu");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{article?.id ? `Eğitimi Düzenle · v${article.version}` : "Yeni Eğitim"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">Kaydettiğinizde version otomatik +0.1 artar. İçerik markdown formatındadır (## başlık, **kalın**, 1. liste).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Başlık</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="article-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Modül</Label>
              <Select value={f.module} onValueChange={(v) => setF({ ...f, module: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="article-module"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-700">
                  {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">İlişkili Ekran (Route)</Label>
              <Select
                value={f.related_route || "__none__"}
                onValueChange={(v) => setF({ ...f, related_route: v === "__none__" ? "" : v })}
              >
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="article-route">
                  <SelectValue placeholder="— Route Yok —" />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-700 max-h-64">
                  <SelectItem value="__none__">— Route Yok —</SelectItem>
                  {routes.filter((r) => r.path).map((r) => (
                    <SelectItem key={r.path} value={r.path}>
                      {r.label} <span className="text-[10px] text-slate-500 font-mono ml-1">{r.path}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Bu Eğitimi Kimler Görsün? <span className="text-slate-500 font-normal">(Rol seçin — hiç seçilmezse Tüm Kullanıcılar)</span></Label>
            <div className="mt-1 p-2 bg-slate-950/40 border border-slate-800 rounded-sm max-h-40 overflow-y-auto grid grid-cols-2 gap-1"
                 data-testid="article-roles-multiselect">
              {availableRoles.map((r) => {
                const checked = (f.roles || []).includes(r.code);
                return (
                  <label key={r.code} className={`flex items-center gap-2 px-2 py-1 rounded-sm text-xs cursor-pointer ${checked ? "bg-blue-950/40 text-blue-200" : "text-slate-300 hover:bg-slate-900"}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleRole(r.code)}
                      data-testid={`article-role-${r.code}`} />
                    <span>{r.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">İçerik (Markdown)</Label>
            <Textarea rows={16} value={f.content_md} onChange={(e) => setF({ ...f, content_md: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1 font-mono text-xs" data-testid="article-content" />
          </div>
          <div>
            <Label className="text-xs">Durum</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="article-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-700">
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="draft">Taslak</SelectItem>
                <SelectItem value="passive">Eski/Pasif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={save} disabled={saving} data-testid="article-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WhatsNewDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ kind: "yeni", title: "", description: "", modules: [], roles: ["*"], date: new Date().toISOString() });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title || !f.description) { toast.error("Başlık + açıklama zorunlu"); return; }
    setSaving(true);
    try {
      await api.post("/learning/whats-new", f);
      toast.success("Yenilik eklendi");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800">
        <DialogHeader><DialogTitle>Yenilik Ekle</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
            <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-700">{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Başlık" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm" />
          <Textarea rows={4} placeholder="Açıklama" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm" />
          <Input placeholder="Modüller (virgülle)" value={f.modules.join(",")} onChange={(e) => setF({ ...f, modules: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="bg-slate-950 border-slate-700 rounded-sm" />
          <Input placeholder="Roller (virgülle, * = herkes)" value={f.roles.join(",")} onChange={(e) => setF({ ...f, roles: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="bg-slate-950 border-slate-700 rounded-sm" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={save} disabled={saving}>Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LearningAdminTab() {
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [reports, setReports] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newsOpen, setNewsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, n, g, r] = await Promise.all([
        api.get("/learning/articles", { params: { include_passive: true } }),
        api.get("/learning/whats-new"),
        api.get("/learning/gaps"),
        api.get("/learning/change-reports"),
      ]);
      setArticles(a.data?.items || []);
      setNews(n.data?.items || []);
      setGaps(g.data?.items || []);
      setReports(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (kind, id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/learning/${kind}/${id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const linkGap = async (gap) => {
    const slug = window.prompt(`"${gap.question}" için bağlanacak eğitim slug'ı:`);
    if (!slug) return;
    const article = articles.find((a) => a.slug === slug);
    if (!article) { toast.error("Slug bulunamadı"); return; }
    try {
      await api.post(`/learning/gaps/${gap.id}/link/${article.id}`);
      toast.success("Gap eğitime bağlandı"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="learning-admin-tab">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm bg-muted/40 border border-border">
          <TabsTrigger value="articles" className="rounded-sm" data-testid="admin-tab-articles"><BookOpen className="w-3.5 h-3.5 mr-1" /> Eğitimler ({articles.length})</TabsTrigger>
          <TabsTrigger value="news" className="rounded-sm" data-testid="admin-tab-news"><Sparkles className="w-3.5 h-3.5 mr-1" /> Yenilikler ({news.length})</TabsTrigger>
          <TabsTrigger value="gaps" className="rounded-sm" data-testid="admin-tab-gaps"><MessageCircleWarning className="w-3.5 h-3.5 mr-1" /> Eksik Konular ({gaps.filter((g) => g.status === "open").length})</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-sm" data-testid="admin-tab-reports"><ListChecks className="w-3.5 h-3.5 mr-1" /> Değişiklik Raporları ({reports.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="articles" className="mt-4 space-y-2">
          <Button className="bg-blue-600 hover:bg-blue-500 rounded-sm" onClick={() => setEditing({})} data-testid="admin-new-article-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni Eğitim
          </Button>
          <Card className="bg-slate-950/60 border-slate-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
                <tr><th className="text-left px-3 py-2">Başlık</th><th className="text-left px-3 py-2">Modül</th><th className="text-left px-3 py-2">Roller</th><th className="text-left px-3 py-2">v</th><th className="text-left px-3 py-2">Durum</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} className="border-b border-slate-900 hover:bg-slate-900/40" data-testid={`admin-article-${a.slug}`}>
                    <td className="px-3 py-2"><div className="text-slate-100">{a.title}</div><div className="text-[10px] text-slate-500 font-mono">{a.slug} · {a.related_route || "—"}</div></td>
                    <td className="px-3 py-2 text-slate-400 font-mono text-xs">{a.module}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{(a.roles || []).join(", ")}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.version}</td>
                    <td className="px-3 py-2"><Badge className={`rounded-sm text-[9px] ${a.status === "active" ? "bg-emerald-950 text-emerald-300 border-emerald-800" : a.status === "passive" ? "bg-slate-900 text-slate-400 border-slate-700" : "bg-amber-950 text-amber-300 border-amber-800"}`}>{a.status}</Badge></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(a)} data-testid={`edit-${a.slug}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="text-rose-400" onClick={() => del("articles", a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="news" className="mt-4 space-y-2">
          <Button className="bg-blue-600 hover:bg-blue-500 rounded-sm" onClick={() => setNewsOpen(true)} data-testid="admin-new-news-btn"><Plus className="w-4 h-4 mr-1" /> Yenilik Ekle</Button>
          {news.map((n) => (
            <Card key={n.id} className="p-3 bg-slate-950/60 border-slate-800 flex items-start gap-2">
              <Badge className="bg-slate-900 border-slate-700 text-slate-300 rounded-sm text-[9px] uppercase">{n.kind}</Badge>
              <div className="flex-1"><div className="text-sm text-slate-100 font-semibold">{n.title}</div><div className="text-xs text-slate-400 mt-1">{n.description}</div><div className="text-[10px] text-slate-500 mt-1 font-mono">{new Date(n.date).toLocaleDateString("tr-TR")} · {(n.modules || []).join(", ")}</div></div>
              <Button variant="ghost" size="sm" className="text-rose-400" onClick={() => del("whats-new", n.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="gaps" className="mt-4 space-y-2">
          <Card className="p-3 border-amber-900 bg-amber-950/20 text-xs text-amber-300">
            Asistanın cevap veremediği veya eğitim bulamadığı sorular otomatik burada listelenir. Yönetici eğitim yazıp <b>bağlayabilir</b>.
          </Card>
          {gaps.map((g) => (
            <Card key={g.id} className={`p-3 border-slate-800 ${g.status === "open" ? "bg-rose-950/20" : "bg-emerald-950/20"}`} data-testid={`gap-${g.id}`}>
              <div className="flex items-start gap-2">
                <Badge className={`rounded-sm text-[9px] uppercase ${g.status === "open" ? "bg-rose-900 text-rose-200" : "bg-emerald-900 text-emerald-200"}`}>{g.status === "open" ? "Açık" : "Çözüldü"}</Badge>
                <div className="flex-1"><div className="text-sm text-slate-100">"{g.question}"</div><div className="text-[10px] text-slate-500 mt-1 font-mono">{g.count} kez sorulmuş · İlk: {new Date(g.first_seen).toLocaleDateString("tr-TR")} · Son: {new Date(g.last_seen).toLocaleDateString("tr-TR")}</div></div>
                {g.status === "open" && (<Button variant="outline" size="sm" className="border-emerald-700 text-emerald-300 rounded-sm" onClick={() => linkGap(g)} data-testid={`link-gap-${g.id}`}><Link2 className="w-3.5 h-3.5 mr-1" /> Eğitime Bağla</Button>)}
                <Button variant="ghost" size="sm" className="text-rose-400" onClick={() => del("gaps", g.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-4 space-y-2">
          {reports.length === 0 && <Card className="p-6 text-center text-slate-500 border-slate-800">Henüz değişiklik raporu yok.</Card>}
          {reports.map((r) => (
            <Card key={r.id} className="p-3 bg-slate-950/60 border-slate-800">
              <div className="text-sm text-slate-100 font-semibold">{r.feature}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-1">{new Date(r.deploy_at).toLocaleString("tr-TR")} · {r.created_by}</div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
                {[
                  ["education_updated", "Eğitim"], ["help_updated", "Yardım"], ["tour_updated", "Tur"],
                  ["assistant_updated", "Asistan"], ["whats_new_updated", "Yenilikler"], ["tested", "Test"],
                ].map(([k, l]) => (
                  <div key={k} className={r[k] ? "text-emerald-400" : "text-rose-400"}>{r[k] ? "✓" : "✗"} {l}</div>
                ))}
              </div>
              {r.notes && <div className="text-xs text-slate-400 mt-2">{r.notes}</div>}
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {editing && <ArticleDialog article={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {newsOpen && <WhatsNewDialog onClose={() => setNewsOpen(false)} onSaved={() => { setNewsOpen(false); load(); }} />}
    </div>
  );
}
